/**
 * RehabProgressSummary — Iter 133.
 *
 * Compact dashboard tile that summarises the patient's rehab progress:
 *   - streak_days · weekly adherence · last VAS score
 *   - "See full report" → /patient/rehab-progress
 *
 * Pulls from the same two endpoints as the full page but keeps the render
 * small so the dashboard stays quick to scan.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Flame, Activity, TrendingUp, ChevronRight, Sparkles } from 'lucide-react';

export default function RehabProgressSummary() {
  const [gam, setGam] = useState(null);
  const [prog, setProg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/hep/gamification/me')
      .then(r => { if (!cancelled) setGam(r.data); })
      .catch(() => { if (!cancelled) setGam({}); });
    api.get('/patients/me')
      .then(r => api.get(`/patients/${r.data.user_id}/progress`))
      .then(r => { if (!cancelled) setProg(r.data); })
      .catch(() => { if (!cancelled) setProg({}); });
    return () => { cancelled = true; };
  }, []);

  const streak = gam?.streak_days ?? 0;
  const weekly = gam?.weekly_done ?? 0;
  const weekly_goal = gam?.weekly_goal ?? 4;
  // Take the last VAS from progress.vas_timeline (if available).
  const vasSeries = prog?.vas_timeline || [];
  const lastVas = vasSeries.length > 0 ? vasSeries[vasSeries.length - 1] : null;

  return (
    <Link
      to="/patient/rehab-progress"
      className="bento-card block hover:shadow-md transition group"
      data-testid="rehab-progress-summary"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-brand/10 grid place-items-center">
            <TrendingUp className="w-4 h-4 text-brand"/>
          </div>
          <div className="font-heading font-semibold text-sm">Rehab progress</div>
        </div>
        <ChevronRight className="w-4 h-4 text-fg-muted group-hover:text-brand transition"/>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Stat icon={Flame} value={streak} label="day streak" color="text-orange-600"/>
        <Stat icon={Activity} value={`${weekly}/${weekly_goal}`} label="this week" color="text-brand"/>
        <Stat icon={Sparkles} value={lastVas?.value != null ? `${lastVas.value}/10` : '—'} label="last VAS" color="text-emerald-700"/>
      </div>
      <div className="text-[11px] text-fg-muted mt-3">Tap to see the full report — streak, adherence and outcome trends in one place.</div>
    </Link>
  );
}

function Stat({ icon: Icon, value, label, color }) {
  return (
    <div className="text-center">
      <div className="text-lg font-heading font-bold inline-flex items-center gap-1">
        <Icon className={`w-4 h-4 ${color}`}/>
        <span className={color}>{value}</span>
      </div>
      <div className="text-[10px] uppercase tracking-wider text-fg-muted mt-0.5">{label}</div>
    </div>
  );
}
